import { useEffect } from 'react';
import {
  applyAudioFxSettings,
  applyNativeElementLoudness,
  applyRadioElementLoudness,
  clearRadioElementLoudness,
  ensureAudioFx,
  preferNativePlaybackAudio,
} from '@/lib/audioFx';
import { usePreferences } from '@/hooks/usePreferences';
import { usePlayer } from '@/hooks/usePlayer';

/** Keeps loudness / EQ in sync — native path uses element.volume for Normalize. */
export function AudioFxSync() {
  const prefs = usePreferences();
  const player = usePlayer();
  const track = player.tracks[player.currentIndex];

  useEffect(() => {
    const audio = player.audioRef.current;
    if (!audio) return;

    // Live: always ReplayGain on the native radio element (ignores Normalize toggle).
    if (player.radioMode) {
      applyRadioElementLoudness(audio, track);
      return;
    }

    // iOS on-demand: no Web Audio — Normalize drives element.volume from ReplayGain.
    if (preferNativePlaybackAudio()) {
      applyNativeElementLoudness(audio, track, prefs.loudnessEnabled);
      return;
    }

    clearRadioElementLoudness(audio);
    ensureAudioFx(audio);
    applyAudioFxSettings(
      {
        loudnessEnabled: prefs.loudnessEnabled,
        lateNightEnabled: prefs.lateNightEnabled,
        eqGains: prefs.eqGains,
      },
      track,
    );
  }, [
    prefs.loudnessEnabled,
    prefs.lateNightEnabled,
    prefs.eqGains,
    track,
    player.audioRef,
    player.currentIndex,
    player.playing,
    player.sessionActive,
    player.radioMode,
  ]);

  return null;
}
